import React, { useState } from 'react';
import { Card, message, theme } from 'antd';
import logo from '../assets/images/logo.png';
import LoginForm from '../components/Login/LoginForm';
import ForgotPasswordForm from '../components/Login/ForgotPasswordForm';
import { useDispatch } from "react-redux";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import {
    doc,
    getDoc,
    collection,
    query,
    where,
    getDocs,
    runTransaction,
} from "firebase/firestore";
import { configActions } from '../redux/config/config';
import { db } from '../data/base';
import { useNavigate } from 'react-router-dom';
import { loginSuccess } from '../redux/authentication/authSlice';
import { setListaCatalogues } from '../redux/Catalogues/actions';
import { secureSetItem } from '../data/utils';
import { fetchAvailableLicenses } from '../services/api/cataloguesApi';
import { setAvailableLicenses } from '../redux/licenses/actions';
import { cleanCorruptedValues } from '../data/utils';

const Login = () => {
    const { token } = theme.useToken();
    const [messageApi, contextHolder] = message.useMessage();
    const [isForgotPassword, setIsForgotPassword] = useState(false);
    const [value, setValue] = useState(localStorage.getItem("profileURL") || "");
    const [email, setEmail] = useState(null);

    function forgotTrue() {
        setIsForgotPassword(true);
    }

    function forgotFalse() {
        setIsForgotPassword(false);
    }

    const navigate = useNavigate();

    const dispatch = useDispatch();
    const auth = getAuth();

    const onSubmit = async (data) => {
        try {
            const normalizedEmail = data.email.toLowerCase(); // Normalizza l'email
            const re = await signInWithEmailAndPassword(auth, normalizedEmail, data.password);
            const idToken = await re.user.getIdToken();

            await createCustomToken(idToken);
            await postLogin(normalizedEmail);
        } catch (error) {
            if (error.code === "auth/multi-factor-auth-required") {
                messageApi.error("MFA required. Please complete the verification.");
            } else {
                messageApi.error(error?.message);
            }
        }
    };

    const postLogin = async (email) => {
        try {
            // Pulisci eventuali valori corrotti dal localStorage
            cleanCorruptedValues();

            // Verifica sia il vecchio che il nuovo profilo
            const userDocRef = doc(db, "Profiles", email.toLowerCase());
            const newUserDocRef = doc(db, "Profiles", email.toLowerCase());
            const [userDoc, newUserDoc] = await Promise.all([
                getDoc(userDocRef),
                getDoc(newUserDocRef)
            ]);

            let userData;
            if (newUserDoc.exists()) {
                userData = newUserDoc.data();
                await handleNewProfileData(userData, email);
            } else if (userDoc.exists()) {
                userData = userDoc.data();
                await handleOldProfileData(userData, email);
            } else {
                throw new Error("User document does not exist");
            }

            // Recupera le informazioni del cliente usando il clientRef dal profilo
            await fetchAndStoreClientInfo(userData);

            const idToken = await auth.currentUser.getIdToken();

            dispatch(loginSuccess({
                user: { uid: auth.currentUser.uid, email },
                token: idToken,
                version: "1.0",
            }));

            setValue("man");
            setEmail(email);

            // Dati comuni a entrambe le versioni
            secureSetItem("role", userData.role || "");
            secureSetItem("email", email.toLowerCase()); // Salva sempre l'email in lowercase
            secureSetItem("ref_cliente", userData.clientRef || "");
            secureSetItem("profileURL", value);
            secureSetItem("Name", userData.nome || "");
            secureSetItem("isUser", true);

            // Fetch and store available licenses including skuLimit
            try {
                const licenses = await fetchAvailableLicenses();
                // Store licenses in Redux
                dispatch(setAvailableLicenses(licenses));
                // Also store in localStorage as a backup
                localStorage.setItem("availableLicenses", JSON.stringify(licenses));
            } catch (licenseError) {
                console.error("Error fetching licenses:", licenseError);
                // Non-blocking error - continue login process
            }

            await writeLoginAccess();
            messageApi.success("Login successful");

            navigate(`${process.env.PUBLIC_URL}/home`);
        } catch (error) {
            console.error("Error during post-login:", error);
            messageApi.error("An error occurred during login. Please contact support.");
        }
    };

    // Funzione per gestire i dati della vecchia struttura (Profile e Brand)
    const handleOldProfileData = async (userData, email) => {
        try {
            // Salva dati specifici di Profile singolarmente
            secureSetItem("o_firstName", userData.firstName || "");
            secureSetItem("o_lastName", userData.lastName || "");
            secureSetItem("o_catalogsList", userData.catalogsList || []);
            secureSetItem("o_ref_log_access", userData.ref_log_access || "");
            secureSetItem("brands", userData.catalogsList || []);

            // Gestione vecchia struttura con Brand
            const brandsSnapshot = await getDocs(collection(db, "Brand"));
            let listaBrands = [];

            if (userData.role === "Admin") {
                // Admin: prende tutti i brand
                listaBrands = brandsSnapshot.docs.map((doc) => doc.id);
            } else {
                // User: filtra brand basandosi su catalogsList
                const tempCatalogsList = userData.catalogsList || [];
                listaBrands = brandsSnapshot.docs
                    .filter((doc) => {
                        const nomeBrand = doc.data()?.nome_brand || "";
                        return !nomeBrand.includes("REV") && tempCatalogsList.includes(doc.id);
                    })
                    .map((doc) => doc.id);
            }

            dispatch(configActions.setListaBrand(listaBrands));

            // Per retrocompatibilità, gestiamo anche i catalogues
            const cataloguesSnapshot = await getDocs(collection(db, "Catalogues"));
            let listaCatalogues = [];

            if (userData.role === "Admin") {
                listaCatalogues = cataloguesSnapshot.docs.map((doc) => doc.id);
            } else {
                const tempCatalogsList = userData.catalogsList || [];
                listaCatalogues = cataloguesSnapshot.docs
                    .filter((doc) => tempCatalogsList.includes(doc.id))
                    .map((doc) => doc.id);
            }

            dispatch(setListaCatalogues(listaCatalogues));
        } catch (error) {
            console.error("Error handling old profile data:", error);
            throw error;
        }
    };

    // Funzione per gestire i dati della nuova struttura (Profiles e Catalogues)
    const handleNewProfileData = async (userData, email) => {
        try {
            // Salva dati specifici di Profiles singolarmente
            secureSetItem("p_firstName", userData.firstName || "");
            secureSetItem("p_lastName", userData.lastName || "");
            secureSetItem("p_list_catalogues", userData.list_catalogues || []);
            secureSetItem("p_list_main_brands", userData.list_main_brands || []);
            secureSetItem("p_refLogAccess", userData.refLogAccess || "");

            // Gestione nuova struttura con Catalogues
            const cataloguesSnapshot = await getDocs(collection(db, "Catalogues"));
            let listaCatalogues = [];

            if (userData.role === "Admin") {
                // Admin: prende tutti i catalogues
                listaCatalogues = cataloguesSnapshot.docs.map((doc) => doc.id);
            } else {
                // User: filtra catalogues basandosi su list_catalogues
                const tempListCatalogues = userData.list_catalogues || [];
                listaCatalogues = cataloguesSnapshot.docs
                    .filter((doc) => tempListCatalogues.includes(doc.id))
                    .map((doc) => doc.id);
            }

            dispatch(setListaCatalogues(listaCatalogues));

            // Gestione brand
            let listaBrands = [];
            const brandsSnapshot = await getDocs(collection(db, "Brand"));

            if (userData.role === "Admin") {
                listaBrands = brandsSnapshot.docs.map((doc) => doc.id);
            } else {
                // Usa list_main_brands se disponibile, altrimenti filtra da Catalogues
                if (userData.list_main_brands && userData.list_main_brands.length > 0) {
                    listaBrands = userData.list_main_brands;
                } else {
                    const tempListCatalogues = userData.list_catalogues || [];
                    listaBrands = brandsSnapshot.docs
                        .filter((doc) => {
                            const nomeBrand = doc.data()?.nome_brand || "";
                            return !nomeBrand.includes("REV") && tempListCatalogues.includes(doc.id);
                        })
                        .map((doc) => doc.id);
                }
            }

            dispatch(configActions.setListaBrand(listaBrands));
        } catch (error) {
            console.error("Error handling new profile data:", error);
            throw error;
        }
    };

    const writeLoginAccess = async () => {
        if (!email) return;

        try {
            const logAccessCollection = collection(db, "Coll_LogAccess");
            const logAccessQuery = query(logAccessCollection, where("account", "==", email));
            const logDocs = await getDocs(logAccessQuery);

            let docRef = null;
            logDocs.forEach((doc) => {
                docRef = doc.ref;
            });

            if (docRef) {
                await runTransaction(db, async (transaction) => {
                    const tDoc = await transaction.get(docRef);
                    if (!tDoc.exists()) return;

                    const logAccess = tDoc.data().listaLog || [];
                    const date = new Date();
                    const newLog = {
                        data: `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()} ${date.getHours()}:${date.getMinutes()}`,
                        dispositivo: navigator.userAgent,
                    };

                    logAccess.push(newLog);
                    transaction.update(docRef, { listaLog: logAccess });
                });
            }
        } catch (error) {
            console.error("Error writing login access:", error);
        }
    };

    const createCustomToken = async (idToken) => {
        try {
            const url = `${process.env.REACT_APP_ACTIVE_BASE_TOKEN}?token=${idToken}`;
            const response = await fetch(url);
            const result = await response.json();

            // Salvataggio sicuro del token
            secureSetItem("csToken", result.csToken);
        } catch (error) {
            console.error("Error creating custom token:", error);
            messageApi.error("Dashboard failed to authenticate");
        }
    };

    // Funzione per recuperare e salvare le informazioni del cliente
    const fetchAndStoreClientInfo = async (userData) => {
        try {
            const clientRef = userData.clientRef;

            if (clientRef && clientRef !== "") {
                secureSetItem("clientId", clientRef);
            } else {
                console.warn("No clientRef found in user profile");
                // Salva null invece di stringa vuota per indicare che non è stato trovato
                secureSetItem("clientId", null);
            }
        } catch (error) {
            console.error("Error processing client info:", error);
            // Salva null invece di stringa vuota in caso di errore
            secureSetItem("clientId", null);
        }
    };

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                background: `radial-gradient(circle at bottom, ${token.colorPrimary} 10%, #241f45 90%)`,
            }}
        >
            {contextHolder}
            <Card
                style={{
                    width: 360,
                    borderRadius: '12px',
                    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
                }}
            >
                <div style={{ textAlign: 'center' }}>
                    <img src={logo} alt="logo" />
                </div>
                {isForgotPassword ? <ForgotPasswordForm setIsForgotPassword={forgotFalse} /> : <LoginForm setIsForgotPassword={forgotTrue} onSubmit={onSubmit} />}
            </Card>
        </div>
    );
};

export default Login;